import { Directive, EventEmitter, HostListener, Input, Output } from '@angular/core';

/**
 * Makes a host element a drag-and-drop file target — the desktop counterpart to
 * the camera button. Adds a `drag-over` class while a drag hovers so the host can
 * style the hover state, and emits the dropped files.
 *
 * Standalone so the lazily-loaded receiving flow can import it directly.
 *
 * Type filtering is left to the consumer (via FilePickerService.filterDropped),
 * which keeps this directive purely about the drag interaction and means the drop
 * path and the file-dialog path share one definition of what's acceptable.
 */
@Directive({
  selector: '[appFileDropTarget]',
  standalone: true,
})
export class FileDropTargetDirective {

  /** Ignores drags entirely when true (e.g. while a capture is already processing). */
  @Input() dropDisabled = false;

  @Output() filesDropped = new EventEmitter<File[]>();

  @HostListener('dragover', ['$event'])
  public onDragOver(event: DragEvent): void {
    if (this.dropDisabled) { return; }
    // Both preventDefault calls are required: without them the browser navigates
    // to the dropped file instead of handing it over.
    event.preventDefault();
    event.stopPropagation();
    this._setDragOver(event, true);
  }

  @HostListener('dragleave', ['$event'])
  public onDragLeave(event: DragEvent): void {
    if (this.dropDisabled) { return; }
    event.preventDefault();
    event.stopPropagation();
    this._setDragOver(event, false);
  }

  @HostListener('drop', ['$event'])
  public onDrop(event: DragEvent): void {
    if (this.dropDisabled) { return; }
    event.preventDefault();
    event.stopPropagation();
    this._setDragOver(event, false);

    const dropped = event.dataTransfer?.files;
    if (!dropped || dropped.length === 0) { return; }
    this.filesDropped.emit(Array.from(dropped));
  }

  private _setDragOver(event: DragEvent, active: boolean): void {
    const host = event.currentTarget as HTMLElement | null;
    host?.classList.toggle('drag-over', active);
  }
}
